import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import './FolderDialog.css';

const FOLDER_COLORS = [
  '#4ECDC4', // Teal (default)
  '#FF6B6B', // Red
  '#4299E1', // Blue
  '#48BB78', // Green
  '#ED8936', // Orange
  '#9F7AEA', // Purple
  '#F6AD55', // Light orange
  '#FC8181', // Light red
  '#68D391', // Light green
  '#63B3ED', // Light blue
];

const FOLDER_ICONS = [
  { id: 'folder', emoji: '📁', label: 'Folder' },
  { id: 'briefcase', emoji: '💼', label: 'Work' },
  { id: 'star', emoji: '⭐', label: 'Important' },
  { id: 'heart', emoji: '❤️', label: 'Favorites' },
  { id: 'bookmark', emoji: '🔖', label: 'Bookmarks' },
  { id: 'archive', emoji: '📦', label: 'Archive' },
  { id: 'music', emoji: '🎵', label: 'Music' },
  { id: 'video', emoji: '🎬', label: 'Video' },
  { id: 'meeting', emoji: '👥', label: 'Meetings' },
  { id: 'personal', emoji: '👤', label: 'Personal' },
  { id: 'work', emoji: '💻', label: 'Work' },
  { id: 'notes', emoji: '📝', label: 'Notes' },
  { id: 'check-circle', emoji: '✅', label: 'Verified' },
];

function FolderDialog({ isOpen, folder, onSave, onDelete, onClose }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(FOLDER_COLORS[0]);
  const [icon, setIcon] = useState('folder');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (folder) {
        setName(folder.name || '');
        setColor(folder.color || FOLDER_COLORS[0]);
        setIcon(folder.icon || 'folder');
      } else {
        setName('');
        setColor(FOLDER_COLORS[0]);
        setIcon('folder');
      }
      setError('');
      setIsSubmitting(false);
      setIsDeleting(false);

      // Focus input after a short delay
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen, folder]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Folder name is required');
      return;
    }

    if (trimmedName.length > 255) {
      setError('Folder name is too long');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSave(trimmedName, color, icon);
    } catch (err) {
      setError(err.message || 'Failed to save folder');
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!folder || !onDelete) return;

    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete the folder "${folder.name}"?\n\nAll transcripts in this folder will be moved to "All Transcripts".`
    );

    if (!confirmed) return;

    setIsDeleting(true);
    setError('');

    try {
      await onDelete(folder.id);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete folder');
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Render using Portal to ensure dialog appears on top
  return createPortal(
    <div className="folder-dialog-overlay" onClick={onClose}>
      <div
        className="folder-dialog"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="folder-dialog-header">
          <h2>{folder ? 'Edit Folder' : 'Create Folder'}</h2>
          <button className="close-btn" onClick={onClose}>
            <XMarkIcon className="close-icon" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="folder-name">NAME</label>
            <input
              ref={inputRef}
              id="folder-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter folder name"
              maxLength={255}
              disabled={isSubmitting || isDeleting}
            />
          </div>

          <div className="form-group">
            <label>COLOR</label>
            <div className="color-picker">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`color-option ${color === c ? 'selected' : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                  disabled={isSubmitting || isDeleting}
                  title={c}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>ICON</label>
            <div className="icon-picker">
              {FOLDER_ICONS.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  className={`icon-option ${icon === i.id ? 'selected' : ''}`}
                  onClick={() => setIcon(i.id)}
                  disabled={isSubmitting || isDeleting}
                  title={i.label}
                >
                  {i.emoji}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="dialog-actions">
            {/* Delete button - only show when editing */}
            {folder && onDelete && (
              <button
                type="button"
                className="delete-btn"
                onClick={handleDelete}
                disabled={isSubmitting || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            
            <div className="dialog-actions-right">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={isSubmitting || isDeleting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="save-btn"
                disabled={isSubmitting || isDeleting || !name.trim()}
              >
                {isSubmitting ? 'Saving...' : folder ? 'Save Changes' : 'Create Folder'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default FolderDialog;
